<script setup>
/* global axios */
// Quick Start — inicia uma conversa só pelo telefone (sem cadastrar contato à
// mão). Botão no header da lista de conversas que abre um popover com telefone +
// caixa + mensagem inicial. Porta o SCRIPT 6 do dashboard injetado.
import { ref, computed, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { frontendURL } from 'dashboard/helper/URLHelper.js';
import NextButton from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const router = useRouter();
const store = useStore();

const accountId = useMapGetter('getCurrentAccountId');
const inboxesGetter = useMapGetter('inboxes/getInboxes');
const inboxes = computed(() => inboxesGetter.value || []);

const open = ref(false);
const phone = ref('');
const inboxId = ref('');
const message = ref('');
const saving = ref(false);
const phoneInput = ref(null);

const toggle = async () => {
  open.value = !open.value;
  if (open.value) {
    if (!inboxes.value.length) store.dispatch('inboxes/get');
    await nextTick();
    phoneInput.value?.focus();
  }
};

const close = () => {
  open.value = false;
};

const submit = async () => {
  const digits = phone.value.replace(/\D/g, '');
  if (digits.length < 8) {
    useAlert(t('CONVERSATION.QUICK_START.INVALID_PHONE'));
    return;
  }
  if (!inboxId.value) {
    useAlert(t('CONVERSATION.QUICK_START.SELECT_INBOX'));
    return;
  }
  saving.value = true;
  try {
    const { data } = await axios.post(
      `/api/v1/accounts/${accountId.value}/kanban/quick_start`,
      { phone: digits, inbox_id: inboxId.value, content: message.value.trim() }
    );
    open.value = false;
    phone.value = '';
    message.value = '';
    router.push(
      frontendURL(
        `accounts/${accountId.value}/conversations/${data.conversation_id}`
      )
    );
  } catch (e) {
    useAlert(e?.response?.data?.error || t('CONVERSATION.QUICK_START.ERROR'));
  } finally {
    saving.value = false;
  }
};
</script>

<template>
  <div v-on-clickaway="close" class="relative">
    <NextButton
      v-tooltip.bottom="t('CONVERSATION.QUICK_START.TOOLTIP')"
      icon="i-lucide-message-square-plus"
      slate
      xs
      faded
      @click="toggle"
    />
    <div
      v-if="open"
      class="absolute z-50 mt-2 w-72 p-3 rounded-xl shadow-lg border border-n-weak bg-n-solid-1 ltr:right-0 rtl:left-0 flex flex-col gap-2"
    >
      <div class="flex items-center justify-between">
        <span class="text-sm font-semibold text-n-slate-12">{{
          t('CONVERSATION.QUICK_START.TITLE')
        }}</span>
        <button
          class="text-n-slate-10 hover:text-n-slate-12 text-lg leading-none"
          @click="close"
        >
          ×
        </button>
      </div>
      <input
        ref="phoneInput"
        v-model="phone"
        type="tel"
        :placeholder="t('CONVERSATION.QUICK_START_PHONE_PLACEHOLDER')"
        class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
        @keydown.enter="submit"
      />
      <div class="relative">
        <select
          v-model="inboxId"
          class="w-full box-border appearance-none !bg-none !mb-0 !h-auto pl-3 pr-8 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
        >
          <option value="">
            {{ t('CONVERSATION.QUICK_START.SELECT_INBOX') }}
          </option>
          <option v-for="i in inboxes" :key="i.id" :value="i.id">
            {{ i.name }}
          </option>
        </select>
        <span
          class="i-lucide-chevron-down pointer-events-none absolute ltr:right-2.5 rtl:left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-n-slate-10"
        />
      </div>
      <textarea
        v-model="message"
        rows="2"
        :placeholder="t('CONVERSATION.QUICK_START.MESSAGE_PLACEHOLDER')"
        class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40 resize-none"
      />
      <NextButton
        :label="
          saving
            ? t('CONVERSATION.QUICK_START.CREATING')
            : t('CONVERSATION.QUICK_START.START')
        "
        blue
        sm
        :is-loading="saving"
        @click="submit"
      />
    </div>
  </div>
</template>
